import { useContext, useEffect, useRef, useState } from "react";
import OwButton from "../../shared/OwButton/OwButton";
import ReactModal from "react-modal";
import ICatalog_Item from "../../../models/ICatalog_Item";
import OwTitle from "../../shared/OwTitle/OwTitle";
import { KitBuilderOption } from "./KitBuilderOption/KitBuilderOption";
import IKit_Builder_Option from "../../../models/IKit_Builder_Option";
import IKit_Builder_Data from "../../../models/IKit_Builder_Data";
import IKit_Builder_Option_ItemUI from "../../../models/IKit_Builder_Option_ItemUI";
import CatalogItemService from "../../../services/CatalogItemService";
import PricingFormulaService from "../../../services/PricingFormulaService";
import "./KitBuilderModal.scss";
import OwLabel from "../../shared/OwLabel/OwLabel";
import HelperService from "../../../services/HelperService";
import SupplierContext from "../../../contexts/SupplierContext";
import VendorContext from "../../../contexts/VendorContext";
import IOrder from "../../../models/IOrder";
import OwToggle from "../../shared/OwToggle/OwToggle";
import EndpointsEnum from "../../../models/EndpointsEnum";
import { SocketContext } from "../../../contexts/SocketContext";
import IKit_Builder_Option_Item_SelectionUI from "../../../models/IKit_Builder_Option_Item_SelectionUI";
import INewOrder_Item_Option from "../../../models/INewOrder_Item_Option";
import { OrderItemOptions } from "../../shared/OrderItemOptions/OrderItemOptions";
import "./KitBuilderModal.scss";

/**
 * Preview price for one selected component. Mirrors the server line calc: when the item has a
 * pricing formula it runs the formula (qty * depth * price / 1000 style, depth = entered length in
 * the item's unit); otherwise it is a plain price * qty. Falls back to price * qty if the formula
 * can't be evaluated so the preview never shows NaN. The authoritative price is still the server's.
 */
const priceForSelection = (catalogItem: ICatalog_Item | undefined, priceGroup: string, qty: number, length?: number): number => {
    const basePrice = Number(catalogItem?.pricing?.[priceGroup]) || 0;
    const expression = catalogItem?.Pricing_Formula?.expression_template;

    if (expression) {
        const result = PricingFormulaService.evaluateFormula(expression, {
            qty,
            price: basePrice,
            depth: Number(length) || 0,
            width: 0,
            height: 0,
        });

        if (result !== undefined) {
            return result;
        }
    }

    return basePrice * qty;
};

type Props = {
    catalogItem: ICatalog_Item;
    /**
     * `itemOptions` is only supplied when `showItemOptions` is set: the kit catalog item's own item
     * option values chosen inside the modal, ready to send as the add DTO's `itemOptionValues`.
     */
    addToCartOrOrder: (kitBuilderSelections: IKit_Builder_Data, itemOptions?: Partial<INewOrder_Item_Option>[]) => void;
    disableSelection: boolean;
    orderModel?: IOrder;
    /**
     * So we can track the endpoint that is being used to save the data and close the modal.
     */
    saveEndpointReturn: EndpointsEnum;
    closeModalOnAdd?: boolean;
    /**
     * Pre-populate the builder with an existing kit's selections (edit-an-existing-kit flow).
     */
    initialKitBuilderData?: IKit_Builder_Data;
    /**
     * Open the modal immediately on mount -- used when re-opening to edit (the trigger button lives
     * elsewhere, e.g. next to the kit items in the order grid).
     */
    autoOpenOnMount?: boolean;
    /**
     * Hide the built-in "build_kit" trigger button (edit flow controls open/close externally).
     */
    hideTriggerButton?: boolean;
    /**
     * Notified when the modal closes so the parent can unmount it (edit flow).
     */
    onClose?: () => void;
    /**
     * Editing an existing kit rather than adding a new one (changes the action label to "update").
     */
    editMode?: boolean;
    /**
     * Render the kit catalog item's own item options (catalogItem.Options) inside the modal and hand
     * the selections back as the second argument of addToCartOrOrder. Off by default: standard order
     * entry renders OrderItemOptions outside the modal and merges the values itself. Required options
     * gate the add button alongside the kit selection validation.
     */
    showItemOptions?: boolean;
    /**
     * Existing item option values to pre-fill when `showItemOptions` is on (edit flow). They are
     * treated as selected until the user changes them.
     */
    initialItemOptions?: Partial<INewOrder_Item_Option>[];
};

export const KitBuilderModal = (props: Props) => {
    const modalElem = useRef<HTMLElement | null>(null);
    const supplierModel = useContext(SupplierContext);
    const vendorModel = useContext(VendorContext);

    const socket = useContext(SocketContext)!;

    const [modalIsOpen, setModalIsOpen] = useState(!!props.autoOpenOnMount);
    // This type comes from the IAddToCartDTO
    const [kitSelections, setKitSelections] = useState<IKit_Builder_Data>(props.initialKitBuilderData || {});
    const [isValid, setIsValid] = useState(false);
    const [isActive, setIsActive] = useState(false);
    const [totalPrice, setTotalPrice] = useState<number>(0);
    const [showPrice, setShowPrice] = useState(!!props.catalogItem.kit_add_each_item);
    const [itemOptions, setItemOptions] = useState<Partial<INewOrder_Item_Option>[]>(props.initialItemOptions || []);

    const showItemOptions = !!props.showItemOptions && !!props.catalogItem.Options?.length;

    const priceGroup = props.orderModel ? props.orderModel.Price_Group : vendorModel.Price_Group;

    const onSave = (data: any) => {
        setModalIsOpen(false);
    };

    useEffect(() => {
        modalElem.current = document.getElementById("root"); // Assuming 'root' is the id of your app's root element
        ReactModal.setAppElement(modalElem.current as HTMLElement); // Safely set the app element

        socket.on(props.saveEndpointReturn, onSave);

        return () => {
            socket.off(props.saveEndpointReturn, onSave);
        };
    }, []);

    useEffect(() => {
        const validDetails = CatalogItemService.isKitBuilderSelectionsComplete(props.catalogItem, kitSelections);
        const optionsValid =
            !showItemOptions || CatalogItemService.areRequiredItemOptionsSelected(props.catalogItem, itemOptions).isComplete;

        setIsValid(validDetails.isComplete && optionsValid);
    }, [kitSelections, itemOptions]);

    const closeModal = () => {
        setModalIsOpen(false);
        props.onClose?.();
    };

    const showKitBuilderModal = () => {
        setModalIsOpen(true);
    };

    const updateSelection = (option: IKit_Builder_Option, itemSelections: IKit_Builder_Option_Item_SelectionUI[]) => {
        const kitSelectionKeys = Object.keys(kitSelections);

        const updatedKitSelections: IKit_Builder_Data = {
            ...kitSelections,
        };

        let foundKey = false;

        for (const kitKey of kitSelectionKeys) {
            // const kit = updatedKitSelections[kitKey];

            if (kitKey === option.name) {
                foundKey = true;

                updatedKitSelections[kitKey] = {
                    ...kitSelections[kitKey],
                };

                // @ts-expect-error
                updatedKitSelections[kitKey].selections = itemSelections
                    .filter((itemSelection) => itemSelection.selected)
                    .map((itemSelection) => {
                        return {
                            optionItem: {
                                ...itemSelection.item,
                                id: itemSelection.id,
                            },
                            subOptionSelections: itemSelection.selectedSubItem ? [itemSelection.selectedSubItem] : [],
                            qty: itemSelection.qty || 1, // Include quantity from selection
                            length: itemSelection.length, // Per-line length for length-priced components -> line depth
                        };
                    });
            } else {
                // add the existing kit selections to the new kit selections
                updatedKitSelections[kitKey] = kitSelections[kitKey];
            }
        }

        if (!foundKey) {
            // we need to add the last kit seleciton item.
            // @ts-expect-error
            updatedKitSelections[option.name] = {
                // @ts-expect-error
                selections: itemSelections
                    .filter((itemSelection) => itemSelection.selected)
                    .map((itemSelection) => {
                        return {
                            optionItem: {
                                ...itemSelection.item,
                                id: itemSelection.id,
                            },
                            subOptionSelections: itemSelection.selectedSubItem ? [itemSelection.selectedSubItem] : [],
                            qty: itemSelection.qty || 1, // Include quantity from selection
                            length: itemSelection.length, // Per-line length for length-priced components -> line depth
                        };
                    }),
            };
        }

        setKitSelections(updatedKitSelections);
    };

    useEffect(() => {
        let calculatedTotalPrice = 0;

        const kitSelectionKeys = Object.keys(kitSelections);

        const _priceGroup = props.orderModel?.Price_Group?.value || vendorModel.Price_Group?.value || "";

        if (props.catalogItem.kit_add_each_item) {
            for (const kitKey of kitSelectionKeys) {
                kitSelections[kitKey].selections.forEach((itemSelection) => {
                    const qty = itemSelection.qty || 1;
                    calculatedTotalPrice += priceForSelection(
                        itemSelection.optionItem.Catalog_Item,
                        _priceGroup,
                        qty,
                        itemSelection.length
                    );

                    (itemSelection.subOptionSelections || []).forEach((subItemSelection) => {
                        calculatedTotalPrice += priceForSelection(subItemSelection.Catalog_Item, _priceGroup, qty, undefined);
                    });
                });
            }
        } else {
            calculatedTotalPrice = Number(props.catalogItem.pricing[_priceGroup]) || 0;
        }

        setTotalPrice(calculatedTotalPrice);
    }, [kitSelections]);

    const addKit = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        e.stopPropagation();

        props.addToCartOrOrder(kitSelections, showItemOptions ? itemOptions : undefined);
        setIsActive(true);

        if (props.closeModalOnAdd || props.editMode) {
            closeModal();
        }
    };

    useEffect(() => {
        if (!modalIsOpen && isActive) {
            setKitSelections({});
            // ReactModal unmounts its children on close, so the OrderItemOptions picker remounts fresh on reopen.
            setItemOptions([]);
        }
    }, [modalIsOpen, isActive]);

    const toggleShowPrice = () => {
        setShowPrice(!showPrice);
    };

    return (
        <div className={"KitBuilderModal"}>
            {!props.hideTriggerButton ? <OwButton title={"build_kit"} fn={showKitBuilderModal} disabled={props.disableSelection} /> : null}

            <ReactModal
                isOpen={modalIsOpen}
                className="KitBuilderModal modal modal-dialog d-flex align-items-center justify-content-center"
                overlayClassName="modal-backdrop modal-no-backdrop-z-index">
                <div className="modal-content bg-dark">
                    {/* Fixed Header */}
                    <div className="modal-header">
                        <div className="w-100 d-flex align-items-center">
                            <OwTitle title={props.catalogItem.name} disableTranslation={true} leftIconName="fas fa-boxes" />
                            {props.catalogItem.kit_add_each_item ? (
                                <span className={"ms-2"}>
                                    <OwToggle
                                        fn={toggleShowPrice}
                                        labelOnRight={true}
                                        name={"toggle-show-price"}
                                        label={"show_price"}
                                        active={showPrice}
                                    />
                                </span>
                            ) : null}
                        </div>
                        <OwButton className="btn-danger ms-auto" centerIconClass="fas fa-times" fn={closeModal} />
                    </div>

                    {/* Scrollable Content */}
                    <div className="modal-body" style={{ maxHeight: "20rem", overflowY: "auto" }}>
                        {props.catalogItem.Kit_Builder_Options?.map((option: IKit_Builder_Option, index: number) => (
                            <KitBuilderOption
                                key={option.guid}
                                option={option}
                                updateFn={updateSelection}
                                showPrice={showPrice}
                                priceGroupName={priceGroup?.value}
                                initialSelections={props.initialKitBuilderData?.[option.name]?.selections}
                            />
                        ))}

                        {showItemOptions ? (
                            <div className={"kit-item-options mt-3 pt-2 border-top"}>
                                <OrderItemOptions
                                    catalogItem={props.catalogItem}
                                    selectedItemOptionsFn={setItemOptions}
                                    flexDirection={"flex-column"}
                                    initialValues={props.initialItemOptions}
                                />
                            </div>
                        ) : null}
                    </div>

                    {/* Fixed Footer */}
                    <div className="modal-footer d-flex justify-content-between">
                        <div className={"d-flex align-items-center border rounded-2 p-2"}>
                            <OwLabel title={"total"} postText={":"} className={"me-2"} isBold={true} />
                            <span className={""}>
                                {HelperService.formatCurrency({ value: totalPrice, precision: supplierModel.pricing_precision || 2 })}
                            </span>
                        </div>
                        <OwButton
                            title={props.editMode ? "update" : "add"}
                            fn={addKit}
                            disabled={!isValid}
                            active={isActive}
                            disableOnActive={isActive}
                            className="btn-primary"
                        />
                    </div>
                </div>
            </ReactModal>
        </div>
    );
};
